import { ChangeEvent, FC, KeyboardEvent, useCallback, useState } from 'react';
import {
  Input,
  Stack,
  Button as DSButton,
  BodyText,
} from '@servicetitan/design-system';
import { Button as AnvilButton, TextField } from '@servicetitan/anvil2';
import SearchIcon from '@servicetitan/anvil2/assets/icons/material/round/search.svg';
import { JobIdLookupAppearance } from './appearance';

export interface JobNumberSearchFieldLocators {
  /** DOM id applied to the search input. */
  input?: string;
  /** DOM id applied to the search-trigger button. */
  button?: string;
}

export interface JobNumberSearchFieldProps {
  /** Fires on every keystroke with the raw input value. */
  onChange: (value: string) => void;
  /** Current input value (controlled). */
  value: string;
  /** Fires when the agent confirms the entered job number (button click or Enter). */
  onSearch: () => void;
  /**
   * Parent-driven validity signal — set to `true` when the form-level
   * validator decides the field must be highlighted (e.g., agent tried to
   * save with no job selected). The component renders an error when this is
   * `true` OR when the local "user submitted an empty input" flag is set;
   * the parent can flip this back to `false` to clear its half of the signal
   * without waiting for the user to type.
   */
  invalidToSubmit?: boolean;
  /**
   * Parent-driven "this job is already linked to another call" flag. Shows
   * the linked-job error message instead of the required-field message.
   */
  isAlreadyLinked?: boolean;
  /** DOM ids for E2E selectors. See `JobNumberSearchFieldLocators`. */
  locators?: JobNumberSearchFieldLocators;
  /**
   * Visual variant — `classic` keeps the design-system primitives used by the
   * Conversation reclassification flow, `anvil2` swaps to TextField + suffix
   * icon-button so the field matches Anvil2-styled forms like the STL outcome
   * gate.
   */
  appearance?: JobIdLookupAppearance;
}

const REQUIRED_MESSAGE = 'Job number is required';
const ALREADY_LINKED_MESSAGE =
  'This job is already linked to another call. Please search for a different one.';

/**
 * Number-entry input + search trigger used by `JobIdLookup`. Manages its own
 * local "empty input" validation flag and combines it with the parent's
 * `invalidToSubmit` prop to render the required-field error.
 *
 * The `classic` appearance renders the design-system Input with an adjacent
 * search button (preserves the Conversation reclassification UI). The
 * `anvil2` appearance renders the Anvil2 `TextField` with the search button
 * as an in-field suffix so heights, borders, and focus rings match the
 * surrounding Anvil2 form controls.
 */
export const JobNumberSearchField: FC<JobNumberSearchFieldProps> = ({
  value,
  onChange,
  onSearch,
  invalidToSubmit,
  isAlreadyLinked,
  locators,
  appearance = 'classic',
}) => {
  // Tracks only the "user-side" half of the validity signal: they cleared
  // the input or clicked search with an empty value. The parent's
  // `invalidToSubmit` is kept entirely separate and combined into
  // `isInvalid` below — this avoids the one-way `useEffect` mirror that
  // would let the local flag stay `true` after the parent had already
  // cleared its prop.
  const [hasLocalEmptyInputError, setHasLocalEmptyInputError] = useState(false);

  const handleSearchValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const isEmpty = event.target.value.trim().length === 0;
      setHasLocalEmptyInputError(isEmpty);
      onChange(event.target.value);
    },
    [onChange],
  );

  const handleSearch = useCallback(() => {
    if (value.trim().length > 0) {
      setHasLocalEmptyInputError(false);
      onSearch();
      return;
    }

    setHasLocalEmptyInputError(true);
  }, [value, onSearch]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSearch();
      }
    },
    [handleSearch],
  );

  // Display the required-field error if either the local empty-input flag
  // is set OR the parent has flagged the field invalid. OR (not AND) is
  // intentional — each signal stands alone: the parent can mark the field
  // invalid without the user ever interacting with it (e.g. they hit Save
  // first), and the user can fail validation locally without the parent
  // knowing.
  const isInvalid = hasLocalEmptyInputError || !!invalidToSubmit;

  const errorMessage = (() => {
    if (isInvalid) return REQUIRED_MESSAGE;
    if (isAlreadyLinked) return ALREADY_LINKED_MESSAGE;
    return null;
  })();

  if (appearance === 'anvil2') {
    return (
      <TextField
        id={locators?.input}
        placeholder='Search by job number'
        value={value}
        error={errorMessage ?? undefined}
        onChange={handleSearchValueChange}
        onKeyDown={handleKeyDown}
        suffix={
          <AnvilButton
            id={locators?.button}
            appearance='ghost'
            size='small'
            icon={SearchIcon}
            aria-label='search-button'
            onClick={handleSearch}
          />
        }
      />
    );
  }

  return (
    <Stack direction='column'>
      <Stack spacing='1'>
        <Stack.Item fill={true}>
          <Input
            id={locators?.input}
            className='d-f'
            placeholder='Search by job number'
            size='small'
            value={value}
            error={isInvalid || isAlreadyLinked}
            onChange={handleSearchValueChange}
            onKeyDown={handleKeyDown}
          />
        </Stack.Item>

        <Stack.Item>
          <DSButton
            id={locators?.button}
            size='small'
            aria-label='search-button'
            iconName='search'
            onClick={handleSearch}
          />
        </Stack.Item>
      </Stack>

      {(isInvalid || isAlreadyLinked) && (
        <BodyText size='small' subdued className='c-red-500 m-t-1'>
          {isInvalid && REQUIRED_MESSAGE}
          {isAlreadyLinked && ALREADY_LINKED_MESSAGE}
        </BodyText>
      )}
    </Stack>
  );
};
